/**
 * Market tools — English copy.
 *
 * Wording rules (product commitments, not style preferences):
 *  - Three neutral price positions only: within / above most registered deals /
 *    below most registered deals. Never "overpriced", never "a deal", never "fair price".
 *  - Every range is built from actually registered deeds, with the deed count and
 *    period shown next to it rather than in small print.
 *  - "A statistical indicator, not a valuation" on every result carrying a price.
 */
const marketTools = {
  page: {
    eyebrow: 'Market data',
    title: 'Market tools',
    subtitle:
      'Pick a district or enter your size, and get an answer built from sale deeds registered with the Ministry of Justice.',
    disclaimer: 'A statistical indicator, not a valuation',
    unavailable: {
      title: 'Tools are not available right now',
      body: 'The market data is not loaded on the server at the moment. Please try again later.',
    },
    stats: {
      districts: 'districts',
      cities: 'cities',
      deeds: 'registered deeds',
      updated: 'Last updated',
    },
  },

  // The "analysing" state — real steps the model takes, not decoration
  analysing: {
    twins: [
      'Reading {{districts}} districts…',
      'Comparing character: sizes, activity, trend, what is nearby…',
      'Ranking the closest matches…',
      'Dropping the dearer ones, keeping the cheaper…',
    ],
    size: [
      'Locating the district…',
      'Finding the size band your area falls into…',
      'Building the price range from registered deeds…',
    ],
    month: [
      'Reading deed dates…',
      'Removing year-on-year market growth…',
      'Measuring each month’s activity…',
    ],
  },

  common: {
    how: 'How this works',
    city: 'City',
    district: 'District',
    chooseCity: 'Choose a city',
    chooseDistrict: 'Choose a district',
    propertyType: 'Property type',
    perM2: 'SAR / m²',
    sar: 'SAR',
    deeds_one: '1 deed',
    deeds_other: '{{count}} deeds',
    basedOn: 'Based on {{count}} registered deeds',
    source: 'Source: Ministry of Justice - registered sale deeds',
    tryAgain: 'Try again',
    change: 'Change',
    noData: 'Not enough data',
    noDataBody:
      'There are not enough registered deeds in this district to answer reliably. Try another district.',
    districtUnknown: 'We did not recognise that district',
    districtUnknownBody: 'Pick the district from the list so we can match it to the deed data.',
    segments: {
      apartment: 'Apartments',
      res_land: 'Residential land',
      com_land: 'Commercial land',
    },
  },

  // 1 — cheaper lookalikes
  twins: {
    name: 'Cheaper lookalike districts',
    tagline: 'Pick a district and see districts that behave like it but cost less.',
    cta: 'Find cheaper lookalikes',
    ctaAgain: 'Try another district',
    howItWorks:
      'Every district is compared on its usual plot sizes, how much trades there, which way prices are moving, and how close the metro, schools and parks are - then the dearer ones are dropped. Price itself is deliberately left out of the comparison, or it would simply return districts at the same price.',
    originTitle: 'The district you picked',
    resultsTitle: 'Similar in character, lower in price',
    resultsCount: '{{count}} districts',
    cheaperBy: '{{pct}}% cheaper',
    medianLabel: 'Median price per m²',
    why: 'Why it is alike',
    reasons: {
      size: 'similar plot sizes',
      activity: 'similar sales activity',
      trend: 'same price direction',
      metro: 'both near the metro',
      schools: 'schools nearby',
      parks: 'parks nearby',
      malls: 'malls nearby',
      profile: 'closest overall match',
    },
    momentum: {
      rising: 'prices rising',
      falling: 'prices falling',
      steady: 'prices steady',
      unclear: 'direction unclear',
    },
    viewListings: 'Browse listings in this district',
    empty: 'No cheaper district is close enough in character to this one.',
  },

  // 2 — price per m² for your size
  size: {
    name: 'Price per m² for your size',
    tagline: 'Enter an area and see what properties that size actually registered for.',
    cta: 'Show the range',
    ctaAgain: 'Check another size',
    howItWorks:
      'Price per m² does not move in a straight line with size - smaller plots usually cost more per metre. So the district’s deeds are split into size bands, and you get the band your area falls into.',
    areaLabel: 'Area (m²)',
    areaPlaceholder: 'e.g. 437',
    priceLabel: 'Asking price (optional)',
    pricePlaceholder: 'e.g. 1,250,000',
    priceHint: 'Add a price to see where it sits in the range.',
    areaRequired: 'Enter an area first.',
    areaInvalid: 'Enter a valid area greater than zero.',
    resultTitle: 'Properties this size in {{place}}',
    bandLabel: 'Size band {{from}} - {{to}} m²',
    rangeTitle: 'Price per m² for this band',
    typical: 'Median',
    halfBetween: 'Half the deals between {{low}} and {{high}}',
    impliedTitle: 'What your area comes to at these prices',
    impliedNote: 'A range built from registered deeds - not a valuation of your property.',
    yourPrice: 'Your price',
    premiumUp: 'This band runs {{pct}}% dearer per m² than the district overall',
    premiumDown: 'This band runs {{pct}}% cheaper per m² than the district overall',
    premiumFlat: 'This band is close to the district average per m²',
    cityFallback:
      'Not enough deeds in this district alone, so the range is for {{city}} as a whole.',
    outOfRange:
      'Your area sits outside the sizes recorded in this district - the nearest available band is shown.',
    allBands: 'Every size band in this district',
    position: {
      within: 'Within the usual range',
      above: 'Above most registered deals',
      below: 'Below most registered deals',
      withinNote: 'Your price sits inside the range half this band’s deals registered at.',
      aboveNote:
        'Your price is above the range half this band’s deals registered at. The property may have features the deed data cannot see.',
      belowNote: 'Your price is below the range half this band’s deals registered at.',
    },
  },

  // 3 — best month to list
  month: {
    name: 'Best month to list',
    tagline: 'Pick a city and see when its market is busiest.',
    cta: 'Show the year',
    ctaAgain: 'Try another city',
    howItWorks:
      'Each month is measured as its share of its own year’s deals, not its raw count - otherwise a growing market would make the latest months look busiest every time. The result averages several whole years.',
    resultTitle: 'Activity through the year in {{city}}',
    busiest: 'Busiest',
    quietest: 'Quietest',
    busiestLine:
      '{{month}} is the busiest month in {{city}} - {{pct}}% more deals than an average month.',
    quietestLine: '{{month}} is the quietest, {{pct}}% below average.',
    vsAverage: 'vs an average month',
    notClear: {
      title: 'No clear difference between months',
      body:
        'The gaps between months in this city are very small - not enough to recommend one. Timing barely matters here.',
    },
    basedOnYears: 'Based on {{years}} whole years and {{deeds}} deeds',
    caveat:
      'This measures selling activity (how many deals), not prices. The calendar is Gregorian, so Ramadan and Hajj shift between months from year to year.',
    months: {
      1: 'January',
      2: 'February',
      3: 'March',
      4: 'April',
      5: 'May',
      6: 'June',
      7: 'July',
      8: 'August',
      9: 'September',
      10: 'October',
      11: 'November',
      12: 'December',
    },
  },
};

export default marketTools;
